import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  Delete as DeleteIcon,
  DragIndicator,
  VisibilityOff,
  Tune as ConditionIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ContentCopy as DuplicateIcon,
} from '@mui/icons-material';

const SortableComponent = ({
  id,
  component,
  isSelected,
  isHovered,
  isHidden,
  hasCondition,
  validation,
  onClick,
  onDelete,
  onDuplicate,
  children,
  sortableData,
  isDraggable = true,
  isDeletable = true,
}) => {
  const hasErrors = (validation?.errors?.length || 0) > 0;
  const hasWarnings = (validation?.warnings?.length || 0) > 0;
  const validationTooltip = validation
    ? [...(validation.errors || []), ...(validation.warnings || [])].join('\n')
    : '';
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: sortableData || {
      type: 'sortable-component',
      component,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      className={`canvas-component ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''} ${isHidden ? 'hidden-component' : ''}${hasErrors ? ' has-validation-error' : ''}`}
      data-testid={`canvas-component-${component.type}`}
      onClick={onClick}
      sx={{
        borderColor: hasErrors
          ? 'error.main'
          : isSelected
            ? 'primary.main'
            : isHovered
              ? 'primary.light'
              : '#ececec',
        backgroundColor: isSelected ? 'rgba(79, 70, 229, 0.04)' : 'white',
        opacity: isHidden ? 0.6 : 1,
        position: 'relative',
      }}
    >
      {/* Meta row: type label + status on the left, hover actions on the right */}
      <Box className="canvas-component__meta">
        <Box className="canvas-component__meta-info">
          <Typography component="span" className="canvas-component__type-label">
            {component.type}
          </Typography>

          {(hasErrors || hasWarnings) && (
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{validationTooltip}</span>}>
              <Box
                component="span"
                className="canvas-component__status"
                data-testid={`canvas-component-validation-${component.type}`}
                sx={{ color: hasErrors ? 'error.main' : 'warning.main' }}
              >
                {hasErrors ? <ErrorIcon fontSize="small" /> : <WarningIcon fontSize="small" />}
              </Box>
            </Tooltip>
          )}

          {hasCondition && !isHidden && (
            <Tooltip title="Only shown when a condition is met">
              <Box component="span" className="canvas-component__status" sx={{ color: 'primary.main' }}>
                <ConditionIcon fontSize="small" />
              </Box>
            </Tooltip>
          )}

          {isHidden && (
            <Tooltip title="Hidden on this screen">
              <Box component="span" className="canvas-component__status" sx={{ color: 'text.disabled' }}>
                <VisibilityOff fontSize="small" />
              </Box>
            </Tooltip>
          )}
        </Box>

        <Box className="canvas-component__actions">
          <IconButton
            {...attributes}
            {...(isDraggable ? listeners : {})}
            size="small"
            className="canvas-component__drag-handle"
            disabled={!isDraggable}
            aria-label="Drag to reorder component"
            sx={{ cursor: isDraggable ? 'grab' : 'default' }}
          >
            <DragIndicator fontSize="small" />
          </IconButton>
          {onDuplicate && (
            <Tooltip title="Duplicate (⌘D)">
              <IconButton
                size="small"
                aria-label="Duplicate component"
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate();
                }}
              >
                <DuplicateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {isDeletable && (
            <IconButton
              size="small"
              aria-label="Delete component"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="canvas-component__delete"
              sx={{ color: 'error.main', '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.08)' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Component Content */}
      <Box className="canvas-component__content">
        {children}
      </Box>
    </Box>
  );
};

export default SortableComponent;